import { Directive, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output } from '@angular/core';

/** Share of the row that must be in view, and for how long, before the ask counts as seen. */
const SEEN_RATIO = 0.6;
export const SEEN_DWELL_MS = 1000;

/**
 * Emits `askSeen` once, when an asking vote row has actually been on screen:
 * mostly in view for a second while the browser tab is visible. A row scrolled
 * out of view, a hidden drawer or a background tab never counts.
 */
@Directive({
    selector: '[gableAskSeen]',
    standalone: true,
})
export class GableAskSeenDirective implements OnChanges, OnDestroy {
    /** Whether the row is asking right now. */
    @Input() gableAskSeen = false;
    @Output() readonly askSeen = new EventEmitter<void>();

    private _observer: IntersectionObserver | null = null;
    private _inView = false;
    private _timer: ReturnType<typeof setTimeout> | null = null;
    private _done = false;
    private readonly _onVisibility = () => this._arm();

    constructor(private readonly _el: ElementRef<HTMLElement>, private readonly _zone: NgZone) {}

    ngOnChanges(): void {
        if (this.gableAskSeen && !this._done) {
            this._watch();
        } else {
            this._stop();
        }
    }

    ngOnDestroy(): void {
        this._stop();
    }

    private _watch(): void {
        if (this._observer || typeof IntersectionObserver === 'undefined') {
            return;
        }
        this._zone.runOutsideAngular(() => {
            this._observer = new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1];
                this._inView = !!entry && entry.isIntersecting && entry.intersectionRatio >= SEEN_RATIO;
                this._arm();
            }, { threshold: [0, SEEN_RATIO, 1] });
            this._observer.observe(this._el.nativeElement);
            document.addEventListener('visibilitychange', this._onVisibility);
        });
    }

    private _arm(): void {
        const visible = !this._done && this._inView && document.visibilityState === 'visible';
        if (!visible) {
            this._clearTimer();
            return;
        }
        if (this._timer) {
            return;
        }
        this._timer = setTimeout(() => {
            this._timer = null;
            this._done = true;
            this._stop();
            this._zone.run(() => this.askSeen.emit());
        }, SEEN_DWELL_MS);
    }

    private _clearTimer(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    private _stop(): void {
        this._clearTimer();
        this._inView = false;
        this._observer?.disconnect();
        this._observer = null;
        document.removeEventListener('visibilitychange', this._onVisibility);
    }
}
